/**
 * /dashboard/maternity-cases/$id — Maternity case detail (G14).
 *
 * Sections (single column, stacked):
 *   1. Header strip — employee, status, vitals, §12 protection badge
 *   2. Statutory timeline (notice → leave start → on leave → §9 ext →
 *      return → closed) with the case's current step highlighted.
 *   3. State-machine actions — next legal transitions; "Mark as
 *      returned" / "Mark leave started" / "Mark as extended (§9)" /
 *      "Withdraw case". Each one opens a dialog that PATCHes the
 *      relevant date AND POSTs to /transition.
 *   4. Editable basic fields — only while status === 'notice_filed'.
 *   5. Check-ins panel — append-only audit trail per Companies Act
 *      §128(5); HR adds outreach / employee updates / medical notes.
 *
 * Statutory footer (small print):
 *   - Maternity Benefit Act 1961 §5  (period — 26 weeks/182 days)
 *   - §9   (illness extension; up to 30 additional days)
 *   - §10  (nursing breaks until child is 15 months old)
 *   - §11A (creche facility — tenant-level §11A annual return)
 *   - §12  (discharge protection during the maternity window)
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useMaternityCase } from "@/hooks/use-maternity";
import { PageHeader } from "@/components/shared/page-header";
import { MaternityRoleGate } from "@/components/maternity/maternity-role-gate";
import { CaseHeader } from "@/components/maternity/case-header";
import { CaseTimeline } from "@/components/maternity/case-timeline";
import { TransitionActions } from "@/components/maternity/transition-actions";
import { EditCasePanel } from "@/components/maternity/edit-case-panel";
import { CheckinsPanel } from "@/components/maternity/checkins-panel";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { statusLabel } from "@/lib/maternity-format";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/maternity-cases/$id",
)({
  component: MaternityCaseDetailRoute,
});

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-64 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

function MaternityCaseDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const q = useMaternityCase(numId, validId);
  const mcase = q.data;

  return (
    <MaternityRoleGate>
      <PageHeader
        title="Maternity case"
        description={mcase ? `Status: ${statusLabel(mcase.status)}` : undefined}
        actions={
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/maternity-cases" params={{ tenantSlug }}>
              <ArrowLeft aria-hidden className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid case id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid maternity-case id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this case</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}{" "}
            <Link
              to="/app/$tenantSlug/maternity-cases" params={{ tenantSlug }}
              className="ml-1 inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to cases
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && mcase && (
        <div className="space-y-5">
          <CaseHeader mcase={mcase} />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Statutory timeline</CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">
                Sequence per Maternity Benefit Act 1961 §5: notice
                received → leave started → 26-week on-leave window
                (with optional §9 illness extension) → return to work.
              </p>
            </CardHeader>
            <CardContent>
              <CaseTimeline mcase={mcase} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Workflow actions</CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">
                Walks the case forward through the statutory state
                machine. Each action stamps the relevant date and flips
                the status in one HR step.
              </p>
            </CardHeader>
            <CardContent>
              <TransitionActions mcase={mcase} />
            </CardContent>
          </Card>

          <EditCasePanel mcase={mcase} />

          <CheckinsPanel caseId={mcase.id} />

          <p className="px-1 text-[11px] leading-relaxed text-muted-foreground">
            Statutory anchors: Maternity Benefit Act 1961 §5 (26-week
            paid leave), §9 (up to 30 days illness extension), §10
            (nursing breaks for 15 months post-delivery), §11A
            (tenant-level creche facility — surfaced in the §11A annual
            return), §12 (discharge protection during the maternity
            window). Check-in entries are append-only per Companies Act
            2013 §128(5).
          </p>
        </div>
      )}
    </MaternityRoleGate>
  );
}
